import { MenuItem, MenuList, Stack } from '@mui/material';
import _ from 'lodash';
import { matchSorter } from 'match-sorter';
import React from 'react';
import ReleaseChip from 'src/components/Chips/ReleaseChip';
import { match } from 'ts-pattern';
import { TextSearchFilter } from '#/components/FormFields';
import type {
  ChatContextOption,
  ChatContextOptionId,
} from './chat-context-options';
import { chatContextOptions } from './chat-context-options';

export interface ChatContextMenuProps {
  /**
   * The callback to call when the active option changes.
   */
  onChangeActiveOption: (option: ChatContextOptionId) => void;
}

function ChatContextMenu({ onChangeActiveOption }: ChatContextMenuProps) {
  const [search, setSearch] = React.useState('');

  const filteredOptions = React.useMemo(() => {
    if (!search) {
      return chatContextOptions;
    }

    return matchSorter(chatContextOptions, search, {
      keys: ['search'],
    });
  }, [search]);

  const handleChangeActiveOption = React.useCallback(
    (option: ChatContextOptionId) => {
      onChangeActiveOption(option);
      setSearch('');
    },
    [onChangeActiveOption]
  );

  return (
    <MenuList id='chat-context-menu'>
      <div className='px-2 w-full mb-1'>
        <TextSearchFilter
          value={search}
          size='xsmall'
          onChange={(text) => setSearch(text)}
          placeholder='Search'
        />
      </div>
      {match(filteredOptions)
        .when(
          (o) => o.length > 0,
          (opts) =>
            opts.map((item: ChatContextOption) => (
              <MenuItem
                key={item.label}
                disableGutters
                disabled={item.status === 'soon'}
                dense
                onClick={() => handleChangeActiveOption(item.value)}
              >
                <Stack
                  direction='row'
                  spacing={1}
                  alignItems='center'
                  className='w-full px-2'
                >
                  {item.icon}
                  <span>{item.label}</span>
                  {item.status !== 'stable' && (
                    <React.Fragment>
                      <div className='flex-1' />
                      <ReleaseChip
                        stage={item.status}
                        size='xsmall'
                        shape='rounded'
                      />
                    </React.Fragment>
                  )}
                </Stack>
              </MenuItem>
            ))
        )
        .otherwise(() => (
          <MenuItem disabled>No results found</MenuItem>
        ))}
    </MenuList>
  );
}

export default ChatContextMenu;
